import React, { useEffect, useId, useLayoutEffect, useReducer, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { clsx } from 'clsx';
import { Button } from './ButtonExtended';

interface DrawerProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    description?: React.ReactNode;
    /** Optional id for the description row (e.g. tour anchor). Defaults to an internal id for aria-describedby. */
    descriptionId?: string;
    children: React.ReactNode;
    /** Optional sticky footer (typically action buttons). For forms in `children`, associate submit buttons via the `form` attribute. */
    footer?: React.ReactNode;
    /** Optional actions beside the drawer title (e.g. drawer-specific tour help). */
    headerActions?: React.ReactNode;
    side?: 'left' | 'right';
    width?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
    /** Use elevated when opening above other modals or popovers */
    stackLevel?: 'default' | 'elevated';
    /** Fires once the enter animation has finished and the panel is fully open. */
    onPanelOpen?: () => void;
    /** Optional id on the sliding panel (e.g. tour highlight for the whole drawer). */
    panelId?: string;
    /** When false, skip the dimmed backdrop (e.g. while a page tour overlay is active). */
    showBackdrop?: boolean;
}

const widthClasses: Record<NonNullable<DrawerProps['width']>, string> = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
    '3xl': 'sm:max-w-3xl',
};

export const DRAWER_PANEL_ENTER_DURATION_MS = 300;
const PANEL_DURATION_MS = DRAWER_PANEL_ENTER_DURATION_MS;
const BACKDROP_DURATION_MS = 300;
const BACKDROP_DELAY_MS = 100;
/** Longest exit path: backdrop delay + fade duration */
const EXIT_DURATION_MS = BACKDROP_DELAY_MS + BACKDROP_DURATION_MS;

type Phase = 'idle' | 'entering' | 'open' | 'exiting';

type Action =
    | { type: 'sync'; isOpen: boolean }
    | { type: 'entered' }
    | { type: 'exitComplete' };

const reducer = (state: Phase, action: Action): Phase => {
    switch (action.type) {
        case 'sync':
            if (action.isOpen) {
                return state === 'idle' || state === 'exiting' ? 'entering' : state;
            }
            return state === 'idle' ? 'idle' : 'exiting';
        case 'entered':
            return state === 'entering' ? 'open' : state;
        case 'exitComplete':
            return state === 'exiting' ? 'idle' : state;
        default:
            return state;
    }
};

export const Drawer: React.FC<DrawerProps> = ({
    isOpen,
    onClose,
    title,
    description,
    descriptionId: descriptionIdProp,
    children,
    footer,
    headerActions,
    side = 'right',
    width = 'lg',
    stackLevel = 'default',
    onPanelOpen,
    panelId,
    showBackdrop = true,
}) => {
    const titleId = useId();
    const generatedDescId = useId();
    const descId = descriptionIdProp ?? generatedDescId;
    const zClass = stackLevel === 'elevated' ? 'z-[100]' : 'z-50';

    const [phase, dispatch] = useReducer(reducer, isOpen ? 'entering' : 'idle');
    const [tourPanelReady, setTourPanelReady] = React.useState(false);
    const previousIsOpenRef = useRef(isOpen);
    const previousPhaseRef = useRef<Phase>(isOpen ? 'entering' : 'idle');
    const enterRafRef = useRef<number | null>(null);

    useEffect(() => {
        if (previousIsOpenRef.current === isOpen) return undefined;
        previousIsOpenRef.current = isOpen;
        dispatch({ type: 'sync', isOpen });
        return undefined;
    }, [isOpen]);

    useLayoutEffect(() => {
        if (phase !== 'entering') return undefined;

        enterRafRef.current = requestAnimationFrame(() => {
            enterRafRef.current = requestAnimationFrame(() => {
                dispatch({ type: 'entered' });
                enterRafRef.current = null;
            });
        });

        return () => {
            if (enterRafRef.current !== null) {
                cancelAnimationFrame(enterRafRef.current);
                enterRafRef.current = null;
            }
        };
    }, [phase]);

    useEffect(() => {
        if (phase !== 'exiting') return undefined;
        const timer = setTimeout(() => dispatch({ type: 'exitComplete' }), EXIT_DURATION_MS);
        return () => clearTimeout(timer);
    }, [phase]);

    const shouldRender = phase !== 'idle';
    const isPanelOpen = phase === 'open';

    useEffect(() => {
        if (!isPanelOpen) {
            const resetTimer = window.setTimeout(() => setTourPanelReady(false), 0);
            return () => window.clearTimeout(resetTimer);
        }

        const timer = window.setTimeout(() => setTourPanelReady(true), PANEL_DURATION_MS + 24);
        return () => window.clearTimeout(timer);
    }, [isPanelOpen]);

    useEffect(() => {
        if (phase === 'open' && previousPhaseRef.current !== 'open') {
            onPanelOpen?.();
        }
        previousPhaseRef.current = phase;
    }, [phase, onPanelOpen]);

    const handlePanelTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
        if (event.propertyName !== 'transform' || !isPanelOpen) return;
        setTourPanelReady(true);
    };

    useLayoutEffect(() => {
        if (!shouldRender) return undefined;
        const body = document.body;
        const previousOverflow = body.style.overflow;
        const previousPaddingRight = body.style.paddingRight;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            body.style.paddingRight = `${scrollbarWidth}px`;
        }
        return () => {
            body.style.overflow = previousOverflow;
            body.style.paddingRight = previousPaddingRight;
        };
    }, [shouldRender]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    if (!shouldRender) return null;

    const panelOnRight = side === 'right';
    const panelSideClass = panelOnRight ? 'right-0' : 'left-0';
    const panelHiddenClass = panelOnRight ? 'translate-x-full' : '-translate-x-full';

    return createPortal(
        <div
            className={clsx('fixed inset-0', zClass)}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={description ? descId : undefined}
        >
            {showBackdrop ? (
                <div
                    className={clsx(
                        'absolute inset-0 bg-black/70',
                        'transition-opacity ease-out',
                        isPanelOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
                    )}
                    style={{
                        transitionDuration: `${BACKDROP_DURATION_MS}ms`,
                        transitionDelay: `${BACKDROP_DELAY_MS}ms`,
                    }}
                    aria-hidden="true"
                    onClick={isPanelOpen ? onClose : undefined}
                />
            ) : null}

            <div
                id={panelId}
                data-tour-panel-ready={isPanelOpen && tourPanelReady ? 'true' : undefined}
                className={clsx(
                    'absolute inset-y-0 flex w-full flex-col bg-white dark:bg-gray-900 shadow-xl',
                    'transition-transform ease-[cubic-bezier(0.32,0.72,0,1)]',
                    panelSideClass,
                    isPanelOpen ? 'translate-x-0' : panelHiddenClass,
                    widthClasses[width],
                )}
                style={{ transitionDuration: `${PANEL_DURATION_MS}ms` }}
                onTransitionEnd={handlePanelTransitionEnd}
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-start justify-between border-b border-gray-100 dark:border-gray-700 px-4 py-4 sm:px-6 shrink-0">
                    <div className="min-w-0 flex-1 pr-3">
                        <div className="flex min-w-0 items-center gap-1.5">
                            <h2
                                id={titleId}
                                className="min-w-0 truncate text-lg font-semibold text-gray-900 dark:text-white"
                            >
                                {title}
                            </h2>
                            {headerActions}
                        </div>
                        {description && (
                            <div id={descId} className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                                {description}
                            </div>
                        )}
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="shrink-0 text-gray-400 hover:text-gray-500"
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" />
                    </Button>
                </header>

                <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
                    {children}
                </div>

                {footer && (
                    <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-4 sm:px-6 shrink-0">
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};
